/**
 * Webhook Service
 *
 * Handles sending webhook notifications when leads are created.
 * Uses HMAC-SHA256 for payload signing (Edge runtime compatible).
 */

import { supabase } from '@/lib/supabase';

export interface WebhookPayload {
  event: 'lead.created' | 'lead.test';
  timestamp: string;
  data: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    customAnswer?: string;
    referralCode?: string;
    referredBy?: string;
    queuePosition: number;
    source: string;
  };
  project: {
    id: string;
    name: string;
    subdomain: string;
    url: string;
  };
}

interface WebhookConfig {
  webhook_url: string | null;
  webhook_secret: string | null;
  webhook_enabled: boolean;
}

/**
 * Generate HMAC-SHA256 signature using Web Crypto API (Edge compatible)
 */
async function generateSignature(
  payload: string,
  secret: string
): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const messageData = encoder.encode(payload);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

  return `sha256=${hashHex}`;
}

export const webhookService = {
  /**
   * Get webhook configuration for a project
   */
  async getWebhookConfig(
    projectId: string
  ): Promise<{ data: WebhookConfig | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('product_deploy')
        .select('webhook_url, webhook_secret, webhook_enabled')
        .eq('project_id', projectId)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Save webhook configuration for a project
   */
  async saveWebhookConfig(
    projectId: string,
    config: Partial<WebhookConfig>
  ): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('product_deploy')
        .update({
          webhook_url: config.webhook_url,
          webhook_secret: config.webhook_secret,
          webhook_enabled: config.webhook_enabled,
          updated_at: new Date().toISOString(),
        })
        .eq('project_id', projectId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  /**
   * Send webhook notification (non-blocking, fire-and-forget)
   * This function is designed to not throw errors to avoid breaking lead creation
   */
  async send(
    webhookUrl: string,
    payload: WebhookPayload,
    secret?: string | null
  ): Promise<{ success: boolean; statusCode?: number; error?: string }> {
    try {
      const payloadString = JSON.stringify(payload);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Waitlistly-Event': payload.event,
      };

      // Add HMAC signature if secret is provided
      if (secret) {
        const signature = await generateSignature(payloadString, secret);
        headers['X-Webhook-Signature'] = signature;
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers,
        body: payloadString,
      });

      if (!response.ok) {
        console.error(
          `[Webhook] Failed to send to ${webhookUrl}: ${response.status} ${response.statusText}`
        );
        return {
          success: false,
          statusCode: response.status,
          error: response.statusText,
        };
      }

      console.log(`[Webhook] Successfully sent to ${webhookUrl}`);
      return { success: true, statusCode: response.status };
    } catch (error) {
      console.error('[Webhook] Error sending webhook:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Create webhook payload from lead data
   */
  createPayload(
    event: 'lead.created' | 'lead.test',
    leadData: {
      email: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      customAnswer?: string;
      referralCode?: string;
      referredBy?: string;
    },
    queuePosition: number,
    project: {
      id: string;
      name: string;
      subdomain: string;
      customDomain?: string | null;
    }
  ): WebhookPayload {
    // Use custom domain if available, otherwise use subdomain
    const projectUrl = project.customDomain
      ? `https://${project.customDomain}`
      : `https://${project.subdomain}.waitlistly.live`;

    return {
      event,
      timestamp: new Date().toISOString(),
      data: {
        email: leadData.email,
        firstName: leadData.firstName,
        lastName: leadData.lastName,
        phone: leadData.phone,
        customAnswer: leadData.customAnswer,
        referralCode: leadData.referralCode,
        referredBy: leadData.referredBy,
        queuePosition,
        source: 'landing_page',
      },
      project: {
        id: project.id,
        name: project.name,
        subdomain: project.subdomain,
        url: projectUrl,
      },
    };
  },

  /**
   * Send test webhook with sample data
   */
  async sendTestWebhook(
    projectId: string,
    projectName: string,
    subdomain: string,
    customDomain?: string | null
  ): Promise<{ success: boolean; error?: string }> {
    const { data: config, error: configError } =
      await this.getWebhookConfig(projectId);

    if (configError || !config) {
      return { success: false, error: 'Failed to load webhook config' };
    }

    if (!config.webhook_url) {
      return { success: false, error: 'No webhook URL configured' };
    }

    const testPayload = this.createPayload(
      'lead.test',
      {
        email: 'test@example.com',
        firstName: 'Test',
        lastName: 'User',
        phone: '+1234567890',
        customAnswer: 'This is a test webhook',
        referralCode: 'test123',
      },
      1,
      {
        id: projectId,
        name: projectName,
        subdomain,
        customDomain,
      }
    );

    return this.send(config.webhook_url, testPayload, config.webhook_secret);
  },
};
